import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  STORY_CONFIRMATION,
  STORY_RIGHTS_CONFIRMATION,
  STORY_WELCOME,
  submitStoryBodySchema,
  type Consent,
  type PublicStory,
} from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import { getPublicStory, submitPublicStory } from '../api/stories.js';
import { STORE_LINKS, STORE_NAME } from '../brand.js';
import { ChoiceGroup } from '../components/ChoiceGroup.js';
import { PageShell } from '../components/PageShell.js';
import { StoryPhotoPicker } from '../components/StoryPhotoPicker.js';
import { StoryVideoPicker } from '../components/StoryVideoPicker.js';

const CONSENT_CHOICES = [
  { value: 'NAMED', label: 'FOIS may share my story with my first name.' },
  { value: 'ANONYMOUS', label: 'FOIS may share my story anonymously.' },
  { value: 'PRIVATE', label: 'Keep my submission private for FOIS feedback only.' },
] as const;

const UNAVAILABLE_COPY =
  'This story link is no longer available. Please contact FESTIVALSOFINDIASTORE if you would still like to share your celebration.';

export function PublicStoryPage() {
  const { token } = useParams<{ token: string }>();
  const idempotencyKey = useRef(crypto.randomUUID());
  const formRef = useRef<HTMLFormElement>(null);
  const [story, setStory] = useState<PublicStory | undefined>();
  const [pageState, setPageState] = useState<'loading' | 'form' | 'success' | 'unavailable'>('loading');
  const [testimonial, setTestimonial] = useState('');
  const [celebrationMeaning, setCelebrationMeaning] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [locationText, setLocationText] = useState('');
  const [consent, setConsent] = useState<Consent>();
  const [rights, setRights] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [photosBusy, setPhotosBusy] = useState(false);
  const [videosBusy, setVideosBusy] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const [videoCount, setVideoCount] = useState(0);
  const uploadsBusy = photosBusy || videosBusy;

  useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    const referrer = document.createElement('meta');
    referrer.name = 'referrer';
    referrer.content = 'no-referrer';
    document.head.append(robots, referrer);
    return () => {
      robots.remove();
      referrer.remove();
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setPageState('unavailable');
      return;
    }
    getPublicStory(token)
      .then((data) => {
        setStory(data);
        setPageState(data.status === 'DRAFT' ? 'form' : 'success');
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError && (caught.status === 410 || caught.status === 404)) {
          setPageState('unavailable');
          return;
        }
        setPageState('unavailable');
      });
  }, [token]);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!token) {
      return;
    }
    const parsed = submitStoryBodySchema.safeParse({
      testimonial: testimonial.trim() || undefined,
      celebrationMeaning: celebrationMeaning.trim() || undefined,
      displayName: displayName.trim() || undefined,
      locationText: locationText.trim() || undefined,
      consentLevel: consent,
      rightsConfirmation: rights ? true : undefined,
    });
    if (!parsed.success) {
      setError('Add a few words, choose how FOIS may use your story, and confirm the rights statement.');
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      await submitPublicStory(token, parsed.data, idempotencyKey.current);
      setPageState('success');
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        setPageState('success');
        return;
      }
      setError(caught instanceof ApiError ? caught.message : 'Your story could not be submitted.');
    } finally {
      setSubmitting(false);
    }
  }

  if (pageState === 'unavailable') {
    return (
      <PageShell>
        <section className="card mx-auto max-w-xl text-center">
          <h1 className="heading-xl text-crimson">Story unavailable</h1>
          <p className="mt-3 text-charcoal">{UNAVAILABLE_COPY}</p>
          <Link className="btn-primary mt-6" to="/">
            Return home
          </Link>
        </section>
      </PageShell>
    );
  }

  if (pageState === 'loading' || !story) {
    return (
      <PageShell>
        <section className="card mx-auto max-w-xl text-center">
          <p className="text-charcoal">Loading your celebration page…</p>
        </section>
      </PageShell>
    );
  }

  if (pageState === 'success') {
    return (
      <PageShell>
        <section className="card mx-auto max-w-xl text-center">
          <h1 className="heading-xl text-crimson">Thank you</h1>
          <p className="mt-3 text-charcoal">{STORY_CONFIRMATION}</p>
          <div className="mt-6 flex flex-col items-center gap-3">
            <a className="btn-primary" href={STORE_LINKS.shopAll}>
              Return to {STORE_NAME}
            </a>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="card mx-auto max-w-2xl">
        <p className="font-display text-xs uppercase tracking-brand text-kraft">Share Your Celebration</p>
        <h1 className="heading-xl mt-3 text-crimson">{story.productName}</h1>
        <p className="mt-4 text-charcoal">{STORY_WELCOME}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <button
            className="btn-secondary"
            type="button"
            disabled={photoCount >= 5}
            onClick={() => document.getElementById('story-photos')?.click()}
          >
            Add photos
          </button>
          <button
            className="btn-secondary"
            type="button"
            disabled={videoCount >= 5}
            onClick={() => document.getElementById('story-videos')?.click()}
          >
            Add videos
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            Write a testimonial
          </button>
        </div>
        {token ? (
          <>
            <StoryPhotoPicker
              token={token}
              onBusyChange={setPhotosBusy}
              onCountChange={setPhotoCount}
            />
            <StoryVideoPicker
              token={token}
              onBusyChange={setVideosBusy}
              onCountChange={setVideoCount}
            />
          </>
        ) : null}
        <form ref={formRef} className="mt-8 space-y-5" onSubmit={(event) => void onSubmit(event)}>
          <div>
            <label className="field-label" htmlFor="testimonial">
              Tell us about your FOIS experience
            </label>
            <textarea
              id="testimonial"
              className="field-input min-h-32"
              maxLength={1500}
              value={testimonial}
              onChange={(event) => setTestimonial(event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="celebration">
              What made this celebration meaningful?
            </label>
            <textarea
              id="celebration"
              className="field-input min-h-24"
              maxLength={750}
              value={celebrationMeaning}
              onChange={(event) => setCelebrationMeaning(event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="displayName">
              Display name
            </label>
            <input
              id="displayName"
              className="field-input"
              maxLength={80}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="location">
              City and state
            </label>
            <input
              id="location"
              className="field-input"
              maxLength={100}
              value={locationText}
              onChange={(event) => setLocationText(event.target.value)}
            />
          </div>
          <ChoiceGroup
            name="consent"
            legend="How may FOIS use your story?"
            required
            value={consent}
            choices={CONSENT_CHOICES}
            onChange={setConsent}
          />
          <label className="flex items-start gap-3 text-sm text-charcoal">
            <input
              className="mt-1 h-5 w-5"
              type="checkbox"
              checked={rights}
              onChange={(event) => setRights(event.target.checked)}
            />
            <span>{STORY_RIGHTS_CONFIRMATION}</span>
          </label>
          {error ? (
            <p className="text-sm font-semibold text-crimson" role="alert">
              {error}
            </p>
          ) : null}
          <button className="btn-primary" type="submit" disabled={submitting || uploadsBusy}>
            {uploadsBusy ? 'Wait for uploads…' : submitting ? 'Sharing…' : 'Share My Experience'}
          </button>
        </form>
      </section>
    </PageShell>
  );
}
